import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface MobileInstallInstructionsProps {
  onClose: () => void;
}

const MobileInstallInstructions: React.FC<MobileInstallInstructionsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  
  const getStepContent = () => {
    if (isIOS) {
      switch (currentStep) {
        case 1:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.ios.step1.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.ios.step1.description')}
              </p>
            </>
          );
        case 2:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.ios.step2.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.ios.step2.description')}
              </p>
            </>
          );
        case 3:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.ios.step3.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.ios.step3.description')}
              </p>
            </>
          );
        default:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.complete.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.complete.description')}
              </p>
            </>
          );
      }
    } else if (isAndroid) {
      switch (currentStep) {
        case 1:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.android.step1.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.android.step1.description')}
              </p>
            </>
          );
        case 2:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.android.step2.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.android.step2.description')}
              </p>
            </>
          );
        case 3:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.android.step3.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.android.step3.description')}
              </p>
            </>
          );
        default:
          return (
            <>
              <h3 className="text-lg font-bold mb-4">{t('install.complete.title')}</h3>
              <div className="text-center mb-4">
                <div className="bg-gray-200 inline-block rounded-full p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-center mb-4">
                {t('install.complete.description')}
              </p>
            </>
          );
      }
    } else {
      // Default for other devices
      return (
        <>
          <h3 className="text-lg font-bold mb-4">{t('install.title')}</h3>
          <p className="text-center mb-4">
            {t('install.other.description')}
          </p>
          <ul className="list-disc pl-8 mb-4">
            <li>{t('install.other.chrome')}</li>
            <li>{t('install.other.edge')}</li>
            <li>{t('install.other.safari')}</li>
          </ul>
        </>
      );
    }
  };

  const totalSteps = isIOS || isAndroid ? 3 : 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-blue-600">{t('install.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label={t('common.close')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          {getStepContent()}
        </div>

        {(isIOS || isAndroid) && (
          <>
            <div className="flex justify-between mb-6">
              <div className="text-sm text-gray-500">
                {t('install.stepCount', { current: currentStep, total: totalSteps })}
              </div>
              <div className="text-sm text-blue-600">
                {Math.round((currentStep / totalSteps) * 100)}% {t('install.complete')}
              </div>
            </div>

            <div className="bg-gray-200 h-2 rounded-full mb-6">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </>
        )}

        <div className="flex justify-between">
          {currentStep > 1 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
            >
              {t('common.previous')}
            </button>
          )}
          
          <div className="flex-grow"></div>
          
          {currentStep < totalSteps ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('common.next')}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('common.done')}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MobileInstallInstructions; 